<script>
import { mapGetters } from 'vuex';
import ViewportWidth from '../../mixins/ViewportWidth';
import SelectionPlaceholder from './common/SelectionPlaceholder.vue';
import PredictorSelection from './common/PredictorSelection.vue';
import PredictorSliderWrap from './common/PredictorSliderWrap.vue';
import PredictorFooter from './common/PredictorFooter.vue';
import PopupModalWrap from './common/PopupModalWrap.vue';
import Presets from './common/Presets.vue';
import { getCookie } from '@/plugins/cookies';
import saveDataInLocalStorage from '@/utils/saveDataInLocalStorage';

export default {
  name: 'Predictor',
  components: {
    SelectionPlaceholder,
    PredictorSelection,
    PredictorSliderWrap,
    PredictorFooter,
    PopupModalWrap,
    Presets,
  },
  mixins: [ViewportWidth],
  props: {
    eventId: {
      type: [Number, String],
      required: true,
    },
    env: {
      type: String,
      default: () => 'prod',
      validator: value => {
        return ['prod', 'dev', 'staging'].indexOf(value) !== -1;
      },
    },
    returnUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showSliders: true,
      showSavePresetModal: false,
      showSignupModal: false,
      isLoaded: false,
    };
  },
  computed: {
    ...mapGetters({
      direction: 'Predictor/getDirection',
      railPosition: 'Predictor/getRailPosition',
      isLoading: 'Predictor/getLoadingStatus',
      isLoggedIn: 'Predictor/getLogInStatus',
      showModal: 'Predictor/getShowModal',
      selections: 'Predictor/getSelections',
      sliders: 'Predictor/getSliders',
    }),
    isMobile() {
      return this.viewportWidth < 929;
    },
    isClockwise() {
      const { direction, isMobile } = this;
      // in mobile, always show anti clockwise layout
      return isMobile ? false : direction === 'clockwise';
    },
    selectionsSorted() {
      const { isMobile, selections, calculateSelectionWidth } = this;
      const selectionsSorted = isMobile
        ? selections.sort((a, b) => b.percentage - a.percentage)
        : selections.sort((a, b) => a.barrierNumber - b.barrierNumber);
      return selectionsSorted.map(selection => {
        return {
          ...selection,
          width: calculateSelectionWidth(selection.percentage),
        };
      });
    },
  },
  beforeMount() {
    this.fetchPredictorData();
  },
  mounted() {
    this.isLoaded = true;
    this.checkLoginStatus();
    this.handleShowSliders();
    window.addEventListener('resize', this.handleShowSliders);
  },
  methods: {
    checkLoginStatus() {
      const { $store } = this;
      const seshID = getCookie('IsLoggedIn');
      if (seshID) {
        const { env } = this;
        $store.commit('Predictor/SET_LOGIN_STATUS', { isLoggedIn: true });
        $store.dispatch('Predictor/fetchPresetData', { env });
      }
    },
    fetchPredictorData(isHandleReset = false) {
      const { eventId, $store } = this;
      $store.dispatch('Predictor/fetchPredictorData', { eventId, isHandleReset });
    },
    handleShowSliders() {
      this.showSliders = !this.isMobile;
    },
    closeSliders() {
      const { $store } = this;
      const dataForRevert = JSON.parse(localStorage.getItem('data-for-revert'));
      const { selections, sliders } = dataForRevert;
      $store.commit('Predictor/SET_SLIDERS', { sliders });
      $store.commit('Predictor/SET_SELECTIONS', { selections });
      this.showSliders = false;
    },
    clickSetting() {
      const { selections, sliders } = this;
      saveDataInLocalStorage({ itemName: 'data-for-revert', data: { selections, sliders } });
      this.showSliders = true;
    },
    applySetting() {
      this.showSliders = false;
    },
    calculateSelectionWidth(percentage) {
      const selectionWrapWidth = this.selectionWrapWidth();
      const { isMobile } = this;
      const selectionMinWidth = isMobile ? 40 : 80;
      const cushionForMobile = isMobile ? 40 : 0;
      const marginOfSelection = (selectionWrapWidth - selectionMinWidth - cushionForMobile) * percentage;
      return parseInt((selectionMinWidth + marginOfSelection).toFixed(1));
    },
    selectionWrapWidth() {
      const { isMobile, env } = this;
      const WRAP_PADDING = isMobile ? 30 : 94;
      let selectionWrapWidth;
      if (isMobile) {
        selectionWrapWidth = window.innerWidth;
      }

      if (!isMobile) {
        if (env === 'dev') {
          selectionWrapWidth = document.querySelector('.predictor-selection-wrap').clientWidth;
        } else {
          const PUNTERS_CONTAINER_WIDTH = 647;
          selectionWrapWidth = PUNTERS_CONTAINER_WIDTH;
        }
      }

      return selectionWrapWidth - WRAP_PADDING;
    },
    resetPredictor() {
      const { isLoading, $store, fetchPredictorData } = this;
      if (isLoading) return false;
      $store.commit('Predictor/SET_LOADING_STATUS');
      const isHandleReset = true;
      fetchPredictorData(isHandleReset);
    },
  },
};
</script>

<template>
  <div class="predictor-wrap">
    <div class="predictor-primary">
      <transition name="fade">
        <PredictorSliderWrap
          v-show="showSliders"
          :class="{ 'predictor-slider-wrap--loaded': isLoaded }"
          :reset-predictor="resetPredictor"
          :selections="selections"
          :is-mobile="isMobile"
          :env="env"
          @onCloseSliders="closeSliders"
          @onApplySetting="applySetting"
        />
      </transition>
      <div
        :class="[
          { 'predictor-selection-wrap--is-loaded': !isLoading || selections.length !== 0 },
          'predictor-selection-wrap',
        ]"
      >
        <p class="predictor__rail-position" v-html="railPosition"></p>
        <div :class="[{ 'predictor__selections--higher-selection': selections.length < 8 }, 'predictor__selections']">
          <PredictorSelection
            v-for="(selection, index) in selectionsSorted"
            :key="`predictor-selection-${index}`"
            :selection-width="selection.width"
            :is-clockwise="isClockwise"
            :is-mobile="isMobile"
            :silk-url="selection.silkImageUrl"
            :result="selection.result"
            :selection-name="selection.name"
            :barrier-number="selection.barrierNumber"
            :competitor-number="selection.competitorNumber"
            :selection-width-percentage="selection.percentage"
            :ranking="(index + 1) | numberOrdinal"
            :best-odds="selection.fixedWinBestOdds"
            :selection-id="parseInt(selection.selectionId)"
          />
          <template v-if="isLoading && selections.length === 0">
            <SelectionPlaceholder v-for="(item, index) in Array(8)" :key="`selection-placeholder-${index}`" />
          </template>
        </div>
        <span class="predictor-border predictor-border--top"></span>
        <span class="predictor-border predictor-border--bottom"></span>
        <div v-show="isMobile" class="predictor__mobile-setting-btn" @click="clickSetting">
          <img src="https://puntcdn.com/predictor/settings-cog.svg" class="predictor-setting-icon" >
          <span>settings</span>
        </div>
      </div>
    </div>
    <PredictorFooter v-if="!isMobile" :reset-predictor="resetPredictor" @fetchPredictorData="fetchPredictorData" />

    <transition name="modal-fade">
      <PopupModalWrap v-if="showModal" :env="env" :return-url="returnUrl" />
    </transition>
  </div>
</template>
